import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Star } from "@phosphor-icons/react";
import axios from "axios";
import { useBrandFont } from "../lib/brandFonts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABEL_STYLE = {
  vegetariano: "bg-emerald-100 text-emerald-800",
  vegano: "bg-green-100 text-green-800",
  senza_glutine: "bg-amber-100 text-amber-800",
  piccante: "bg-rose-100 text-rose-700",
};

export default function MenuPubblico() {
  const { rid } = useParams();
  const [data, setData] = useState(null);
  const [brand, setBrand] = useState(null);
  useBrandFont(brand?.font || "");
  const [error, setError] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [catPath, setCatPath] = useState([]); // percorso categoria → sottocategoria
  const [excluded, setExcluded] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API}/menu/public/${rid}/v2`)
      .then((r) => {
        setData(r.data);
        const q = searchParams.get("m");
        setMenuId(r.data.menus.find((m) => m.id === q)?.id || r.data.menus[0]?.id || null);
      })
      .catch(() => setError("Menu non disponibile"));
    axios.get(`${API}/public/restaurant/${rid}/brand`).then((r) => setBrand(r.data)).catch(() => {});
  }, [rid]);

  const menu = data?.menus.find((m) => m.id === menuId);
  const allergenLabel = useMemo(
    () => Object.fromEntries((data?.allergens || []).map((a) => [a.key, a.label])), [data]);
  const labelLabel = useMemo(
    () => Object.fromEntries((data?.labels || []).map((l) => [l.key, l.label])), [data]);

  const catDishes = (c) => [...(c.dishes || []), ...(c.children || []).flatMap((ch) => ch.dishes || [])];
  const presentAllergens = useMemo(() => {
    const s = new Set();
    menu?.categories.forEach((c) => catDishes(c).forEach((d) => (d.allergens || []).forEach((a) => s.add(a))));
    return [...s];
  }, [menu]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm" data-testid="mp-error">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm">Caricamento menu…</div>;

  const primary = brand?.colors?.primary || "#01B2E0";
  const theme = {
    logo_url: "", bg_color: "#fafafa", bg_image_url: "", overlay_color: "#000000",
    overlay_opacity: 0, button_color: "#ffffff", button_text_color: "",
    button_border_color: "#e4e4e7", button_radius: "tondi", button_shadow: "leggero",
    text_color: "#18181b", card_color: "", card_border_color: "",
    logo_size: 80, name_size: 24, menu_name_size: 12, category_text_color: "", ...(data.theme || {}),
  };
  const logo = theme.logo_url || (brand?.assets?.logo ? `${process.env.REACT_APP_BACKEND_URL}${brand.assets.logo}` : null);
  const catText = theme.category_text_color || theme.button_text_color || primary;
  const RADII = { quadrati: 4, poco: 8, tondi: 16, pillola: 999 };
  const OMBRE = { nessuno: "none", leggero: "0 1px 3px rgba(0,0,0,.10)", medio: "0 4px 12px rgba(0,0,0,.14)", forte: "0 10px 26px rgba(0,0,0,.22)" };
  const btnStyle = {
    background: theme.button_color, color: catText,
    border: `1.5px solid ${theme.button_border_color}`,
    borderRadius: RADII[theme.button_radius] ?? 16,
    boxShadow: OMBRE[theme.button_shadow] ?? OMBRE.leggero,
  };
  const hexToRgba = (hex, a) => {
    const h = (hex || "#000000").replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  };
  const ovA = Math.min(0.9, Math.max(0, Number(theme.overlay_opacity) || 0));
  const ov = hexToRgba(theme.overlay_color, ovA);

  const visible = (d) => d.available && !(d.allergens || []).some((a) => excluded.includes(a));
  const cats = menu?.categories || [];
  let openCat = null;
  if (catPath.length) {
    let list = cats;
    for (const k of catPath) {
      openCat = list.find((c) => (c.id || c.name) === k) || null;
      if (!openCat) break;
      list = openCat.children || [];
    }
  }
  const openVis = openCat ? (openCat.dishes || []).filter(visible) : [];

  const switchMenu = (id) => {
    setMenuId(id); setCatPath([]);
    setSearchParams(id === data.menus[0]?.id ? {} : { m: id }, { replace: true });
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="min-h-screen pb-16" data-testid="menu-pubblico" data-brand-font="" style={{ backgroundColor: theme.bg_color }}>
      {theme.bg_image_url && (
        <div className="fixed inset-0 pointer-events-none" data-testid="mp-bg-layer"
          style={{ backgroundImage: `url(${theme.bg_image_url})`,
                   backgroundSize: "cover", backgroundPosition: "center" }} />
      )}
      {ovA > 0 && (
        <div className="fixed inset-0 pointer-events-none" data-testid="mp-overlay-layer" style={{ backgroundColor: ov }} />
      )}
      <div className="relative z-10">
      <header className="px-5 pt-8 pb-6 text-center">
        {logo && <img src={logo} alt={data.restaurant.name} className="mx-auto mb-3 object-contain" style={{ height: theme.logo_size }} data-testid="mp-logo" />}
        <h1 className="font-bold tracking-tight" style={{ color: theme.text_color, fontSize: theme.name_size }}>{data.restaurant.name}</h1>
        <p className="mt-1" style={{ color: theme.text_color, opacity: 0.65, fontSize: theme.menu_name_size }}>{menu?.name || "Menu digitale"}</p>
      </header>

      <main className="max-w-xl mx-auto px-4 pt-5">
        {!openCat ? (
          /* ---------- LANDING: bottoni categoria cliccabili ---------- */
          <div className="space-y-3" data-testid="mp-categories-landing">
            {cats.filter((c) => catDishes(c).some((d) => d.available)).length === 0 && (
              <p className="text-sm text-zinc-400 text-center py-8">Nessun piatto disponibile.</p>
            )}
            {cats.map((c) => {
              const count = catDishes(c).filter((d) => d.available).length;
              if (!count) return null;
              return (
                <button key={c.id || c.name} onClick={() => setCatPath([c.id || c.name])}
                  data-testid={`mp-cat-btn-${c.id || c.name}`}
                  style={btnStyle}
                  className="w-full px-5 py-5 text-left transition hover:brightness-95 active:scale-[0.99] flex items-center gap-4">
                  {c.image_url && (
                    <img src={c.image_url} alt="" className="h-14 w-20 object-cover rounded-lg shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-lg font-bold block" style={{ color: catText }}>{c.name}</span>
                    {c.description && <span className="text-xs line-clamp-1" style={{ color: catText, opacity: 0.65 }}>{c.description}</span>}
                  </div>
                  <span className="text-xl shrink-0" style={{ color: catText, opacity: 0.4 }}>›</span>
                </button>
              );
            })}
            {data.menus.filter((m) => m.id !== menuId).map((m) => (
              <div key={m.id} className="pt-5" data-testid={`mp-switch-wrap-${m.id}`}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-center mb-2"
                  style={{ color: theme.text_color, opacity: 0.5 }}>Guarda anche</p>
                <button onClick={() => switchMenu(m.id)} data-testid={`mp-switch-menu-${m.id}`}
                  className="w-full px-5 py-4 rounded-2xl text-white text-base font-bold flex items-center justify-center gap-1.5 transition hover:brightness-110 active:scale-[0.99]"
                  style={{ background: primary }}>
                  {m.name} <span className="text-lg leading-none">›</span>
                </button>
              </div>
            ))}
          </div>
        ) : (
          /* ---------- CATEGORIA APERTA: piatti ---------- */
          <div data-testid="mp-category-view">
            <button onClick={() => { setCatPath((p) => p.slice(0, -1)); window.scrollTo({ top: 0 }); }}
              data-testid="mp-back-categories"
              className="h-10 px-4 rounded-lg text-white text-sm font-bold flex items-center gap-1.5 mb-5"
              style={{ background: primary }}>
              <ArrowLeft size={15} weight="bold" /> Categorie
            </button>

            <h2 className="text-xl font-bold text-center mb-1" style={{ color: primary }}
              data-testid={`mp-category-title-${openCat.id || openCat.name}`}>{openCat.name}</h2>
            {openCat.image_url && (
              <img src={openCat.image_url} alt={openCat.name}
                className="w-full h-40 object-cover rounded-xl my-3" data-testid="mp-category-hero" />
            )}
            {openCat.description && (
              <p className="text-sm text-zinc-500 text-center mb-4">{openCat.description}</p>
            )}

            {(openCat.children || []).length > 0 && (
              <div className="space-y-3 mb-6" data-testid="mp-subcats">
                {openCat.children.map((ch) => {
                  const cnt = catDishes(ch).filter((d) => d.available).length;
                  if (!cnt) return null;
                  return (
                    <button key={ch.id} onClick={() => setCatPath((p) => [...p, ch.id])}
                      data-testid={`mp-subcat-btn-${ch.id}`}
                      style={btnStyle}
                      className="w-full px-5 py-4 text-left transition hover:brightness-95 active:scale-[0.99] flex items-center gap-4">
                      {ch.image_url && (
                        <img src={ch.image_url} alt="" className="h-12 w-16 object-cover rounded-lg shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-base font-bold block" style={{ color: catText }}>{ch.name}</span>
                        {ch.description && <span className="text-xs line-clamp-1" style={{ color: catText, opacity: 0.65 }}>{ch.description}</span>}
                      </div>
                      <span className="text-xl shrink-0" style={{ color: catText, opacity: 0.4 }}>›</span>
                    </button>
                  );
                })}
              </div>
            )}

            {presentAllergens.length > 0 && (
              <div className="py-3">
                <p className="text-[11px] font-semibold text-zinc-500 mb-2">ESCLUDI ALLERGENI:</p>
                <div className="flex gap-1.5 flex-wrap">
                  {presentAllergens.map((a) => (
                    <button key={a} data-testid={`mp-filter-${a}`}
                      onClick={() => setExcluded((x) => x.includes(a) ? x.filter((k) => k !== a) : [...x, a])}
                      className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-colors ${excluded.includes(a) ? "bg-rose-600 text-white border-rose-600" : "bg-white text-zinc-600 border-zinc-300"}`}>
                      {allergenLabel[a] || a}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2.5 mt-2">
              {openVis.length === 0 && (openCat.children || []).length === 0 && (
                <p className="text-sm text-zinc-400 text-center py-8">Nessun piatto disponibile con questi filtri.</p>
              )}
              {openVis.map((d) => (
                <article key={d.id} className="rounded-xl border p-3.5 flex gap-3" data-testid={`mp-dish-${d.id}`}
                  style={{ background: theme.card_color || "#ffffff", borderColor: theme.card_border_color || "#e4e4e7" }}>
                  {d.photo && <img src={d.photo} alt={d.name} className="w-20 h-20 rounded-lg object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-bold flex items-center gap-1.5" style={{ color: primary }}>
                        {d.name}
                        {d.dish_of_day && <Star size={13} weight="fill" className="text-amber-500 shrink-0" />}
                      </h3>
                      <span className="text-sm font-bold shrink-0 text-zinc-900">€{d.price.toFixed(2).replace(".", ",")}</span>
                    </div>
                    {d.description && <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{d.description}</p>}
                    {(d.labels || []).length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-1.5">
                        {d.labels.map((l) => (
                          <span key={l} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${LABEL_STYLE[l] || "bg-zinc-100 text-zinc-600"}`}>
                            {(labelLabel[l] || l).toUpperCase()}
                          </span>
                        ))}
                      </div>
                    )}
                    {(d.specs || []).length > 0 && (
                      <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5" data-testid="mp-specs">
                        {d.specs.map((sp, i) => (
                          <div key={i} className="flex items-baseline justify-between gap-2 py-0.5 border-b border-dotted border-zinc-200">
                            <span className="text-[10px] uppercase tracking-wide font-semibold text-zinc-400 shrink-0">{sp.label}</span>
                            <span className="text-xs text-zinc-700 text-right">{sp.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(d.variants || []).length > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        {d.variants.map((v, i) => (
                          <div key={i} className="flex justify-between text-xs text-zinc-600">
                            <span>{v.name}</span><span className="font-semibold">€{v.price.toFixed(2).replace(".", ",")}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(d.allergens || []).length > 0 && (
                      <p className="text-[10px] text-zinc-400 mt-1.5">
                        Allergeni: {d.allergens.map((a) => allergenLabel[a] || a).join(", ")}
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="text-center text-[11px] text-zinc-400 mt-10" data-testid="mpub-footer">
        In caso di allergie o intolleranze chiedi sempre conferma al personale · Menu digitale con Dishenda ·{" "}
        <a href="/locali" className="underline hover:text-zinc-600 transition-colors" data-testid="mpub-crosspromo">Scopri altri locali</a>
      </footer>
      </div>
    </div>
  );
}
